import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { api, invalidateAuth, readApiResponseError, setAccessToken, setSessionExpiredHandler, setSessionChangedHandler, resumeAfterReauth } from '../lib/api';
import { SessionExpiredModal } from '../components/auth/SessionExpiredModal';
import { clearSourcePreviewCache } from '../hooks/useSourcePreview';
import { clearMatrixSourcePreviewCache } from '../hooks/useMatrixSourcePreview';
import { clearChatAvailabilityCache } from '../lib/chat/availabilityCache';
import { clearBatesLookupCache } from '../lib/chat/bates';
import { clearDrafts } from '../lib/chat/drafts';

import { authRequest, withAuthCookieLock } from '../lib/authTransport';

interface VerifyOtpResult {
  access_token: string | null;
  email: string | null;
  error: Error | null;
}

interface AuthContextType {
  loading: boolean;
  userEmail: string | null;
  signInWithOtp: (email: string) => Promise<{ error: Error | null }>;
  verifyOtp: (email: string, token: string) => Promise<VerifyOtpResult>;
  signOut: () => Promise<void>;
  // Backward compat — App.tsx checks `session` for auth gate
  session: { user: { email: string } } | null;
  user: { email: string } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  // Live mirror of userEmail for the session-expired handler (registered once
  // on mount, so it can't see fresh state through closure).
  const userEmailRef = useRef<string | null>(null);
  userEmailRef.current = userEmail;
  // Identity that owned the session when expiry was detected. Snapshotting at
  // open time (rather than reading userEmailRef in handleReauthSuccess) makes
  // the identity-change check immune to React flushing verifyOtp's setUserEmail.
  const expiredEmailRef = useRef<string | null>(null);
  // Bumped on every sign-out/cancel so a late in-flight OTP verify can't
  // resurrect a session the user just ended (Verify → "Sign out instead" race).
  const authGen = useRef(0);

  useEffect(() => {
    const gen = authGen.current;
    let active = true;
    // A restoration that finishes after sign-out/unmount cannot revive the UI.
    api.tryRestore().then((data) => {
      if (!active || gen !== authGen.current) return;
      if (data) {
        setUserEmail(data.email);
      }
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    // lib/api.ts calls this when /auth/refresh confirms expiry. Show the
    // blocking re-OTP modal — except on /login, where LoginScreen is
    // already the right UI (a stale background fetch 401ing must not pop
    // a modal over the login form), and except when signed out, where
    // there is no session to recover (e.g. a poller tick racing sign-out).
    setSessionChangedHandler(() => {
      authGen.current += 1;
      setUserEmail(null);
      clearSourcePreviewCache();
      clearMatrixSourcePreviewCache();
      clearChatAvailabilityCache();
      clearBatesLookupCache();
      clearDrafts(); // sessionStorage survives the reload
      window.location.reload();
    });
    setSessionExpiredHandler(() => {
      if (typeof window !== 'undefined') {
        const path = window.location.pathname.replace(/\/+$/, '');
        if (path === '/login' || !userEmailRef.current) {
          resumeAfterReauth(false);
          return;
        }
      }
      setSessionExpired(true);
      expiredEmailRef.current = userEmailRef.current;
    });
    // Resolve-then-clear: requests gated on a pending reauth must settle
    // (as failed) rather than hang if the handler goes away.
    return () => {
      resumeAfterReauth(false);
      setSessionExpiredHandler(null);
      setSessionChangedHandler(null);
    };
  }, []);

  const signInWithOtp = async (email: string) => {
    try {
      return await authRequest('/auth/otp/send', {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        credentials: 'include', body: JSON.stringify({email}),
      }, async resp => {
        if (!resp.ok) {
          return {error: await readApiResponseError(resp)};
        }
        return {error: null};
      });
    } catch (err: any) {
      return { error: err as Error };
    }
  };

  const verifyOtp = async (email: string, token: string): Promise<VerifyOtpResult> => {
    const gen = authGen.current;
    const cancelled = () => ({access_token: null, email: null, error: new Error('Request was cancelled')});
    try {
      const result = await withAuthCookieLock(async () => {
        if (gen !== authGen.current) return cancelled();
        return authRequest('/auth/otp/verify', {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          credentials: 'include', body: JSON.stringify({email, code: token}),
        }, async resp => {
          if (gen !== authGen.current) return cancelled();
          if (!resp.ok) {
            const error = await readApiResponseError(resp);
            if (gen !== authGen.current) return cancelled();
            return {access_token: null, email: null, error};
          }
          const data = await resp.json();
          if (gen !== authGen.current) return cancelled();
          if (typeof data?.access_token !== 'string' || !data.access_token ||
              typeof data.email !== 'string' || data.email !== email.toLowerCase().trim()) {
            return {access_token: null, email: null, error: new Error('Unexpected sign-in response. Please try again.')};
          }
          setAccessToken(data.access_token, data.email);
          setUserEmail(data.email);
          // A composer unmounting after the last sign-out can write its draft back; only the
          // signing-in user's drafts may stay in this tab.
          clearDrafts(data.email);
          setSignOutError(null);
          return {access_token: data.access_token, email: data.email, error: null};
        });
      });
      // Lock callback completion and delivery of its result are separate steps.
      // Cancellation during that handoff must never reach a form's onSuccess.
      return gen === authGen.current ? result : cancelled();
    } catch (err: any) {
      return { access_token: null, email: null, error: err as Error };
    }
  };

  const signOut = async () => {
    const gen = ++authGen.current;
    setSignOutError(null);
    // Clear local state FIRST, before the revocation round-trip: any request
    // that 401s in between must see "signed out" (no modal, clean failure)
    // rather than reopening the expiry flow against a dying session. Also bumps
    // the api.ts generation so in-flight requests/refresh results are discarded.
    invalidateAuth();
    setUserEmail(null);
    setLoading(false);
    // Release (as failed) any request gated behind an open re-auth prompt and
    // close it — a sign-out from anywhere must not leave the pipeline hanging.
    setSessionExpired(false);
    resumeAfterReauth(false);
    // Drop the module-level source-preview cache so a different user signing in
    // on the same tab (no full reload) can't read the prior user's cached source
    // summaries — every preview re-fetches under the new session's RLS.
    clearSourcePreviewCache();
    clearMatrixSourcePreviewCache();
    clearChatAvailabilityCache();
    clearBatesLookupCache();
    // Unsent Workbench drafts are the user's own text and live in sessionStorage.
    clearDrafts();
    // Wait behind cookie-writing verification/refresh, even when its JS result
    // was cancelled. Otherwise its late Set-Cookie can undo this sign-out.
    let revoked = false;
    try {
      await withAuthCookieLock(async () => {
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const ok = await authRequest('/auth/refresh/logout', {
              method: 'POST', credentials: 'include',
            }, async resp => resp.ok, 10_000);
            if (ok) { revoked = true; break; }
          } catch {
            // Best effort: retry once with a bounded deadline.
          }
        }
      });
    } catch {
      // A stalled tab can also time out lock acquisition. Local state is cleared.
    }
    if (!revoked && gen === authGen.current) {
      setSignOutError("We couldn't complete sign-out. Please retry to end your session on this browser.");
    }
  };

  const handleReauthSuccess = (newAccessToken: string, email: string) => {
    setAccessToken(newAccessToken, email);
    setUserEmail(email);
    // Re-auth keeps the whole page mounted by design, so drop user-scoped
    // caches unconditionally — and if the identity itself changed (only
    // reachable outside the locked modal email), reload rather than letting
    // the new user inherit the old user's mounted case data.
    clearSourcePreviewCache();
    clearMatrixSourcePreviewCache();
    clearChatAvailabilityCache();
    clearBatesLookupCache();
    // The same user keeps their drafts across a re-auth; anyone else's go.
    clearDrafts(email);
    if (email !== expiredEmailRef.current) {
      // The verified identity differs from the one that expired — do not let
      // it inherit the old user's mounted case data; reload clean.
      resumeAfterReauth(false);
      window.location.reload();
      return;
    }
    expiredEmailRef.current = null;
    setSessionExpired(false);
    resumeAfterReauth(true);
  };

  const handleReauthCancel = async () => {
    setSessionExpired(false);
    resumeAfterReauth(false);
    await signOut();
    // Navigation handled by AuthGate once userEmail clears
  };

  // Backward-compatible session/user objects so App.tsx doesn't need changes
  const session = userEmail ? { user: { email: userEmail } } : null;
  const user = userEmail ? { email: userEmail } : null;

  const value: AuthContextType = {
    loading,
    userEmail,
    signInWithOtp,
    verifyOtp,
    signOut,
    session,
    user,
  };

  return (
    <AuthContext.Provider value={value}>
      <div style={{display: 'contents'}} ref={element => {
        if (!element) return;
        if (sessionExpired) element.setAttribute('inert', '');
        else element.removeAttribute('inert');
      }}>
        {children}
      </div>
      {signOutError && (
        <div role="alert" className="fixed bottom-4 left-4 right-4 z-[250] rounded-lg bg-red-950 border border-red-500 p-4 text-white text-sm">
          <p>{signOutError}</p>
          <button className="mt-2 underline" onClick={() => void signOut()}>Retry sign-out</button>
        </div>
      )}
      <SessionExpiredModal
        open={sessionExpired}
        email={userEmail}
        onSuccess={handleReauthSuccess}
        onCancel={handleReauthCancel}
      />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
